/**
 * Die fünf Zustände als Symbol (7.1 R141/R142, 7.2 R152).
 *
 * Gefüllte Grundformen mit je eigener Silhouette — bewusst anders gezeichnet
 * als die Bereichssymbole in `bereiche.tsx`, die Strichzeichnungen sind. Wer
 * den Bildschirm in Graustufen ausdruckt, unterscheidet sie weiterhin: Farbe
 * verstärkt hier, sie trägt nicht allein (R143).
 *
 * Die konkrete Zeichnung ist ausdrücklich ein Erprobungsvorschlag — 7.1
 * verweist sie an den Benutzertest. Die Silhouettenregel steht fest, die
 * Strichführung nicht.
 */

import { t } from './sprache'

export type Zustandsart = 'fehlt' | 'offen' | 'blockiert' | 'veraltet' | 'fertig'

/**
 * Der Name des Zustands — er steht als `aria-label` am Symbol.
 *
 * Ohne ihn ist die Iconsprache für ein Vorleseprogramm stumm, und R143
 * („Farbe ist nie die einzige Unterscheidung") wäre nur zur Hälfte erfüllt:
 * Form und Farbe tragen für das Auge, der Name trägt für alle anderen.
 */
function benennung(art: Zustandsart): string {
  return t(`zustand.${art}`)
}

export function Zustand({ art, text }: { art: Zustandsart; text?: string }) {
  return (
    <span className={`zustand ${art}`}>
      <Zustandssymbol art={art} />
      <span>{text ?? benennung(art)}</span>
    </span>
  )
}

export function Zustandssymbol({ art }: { art: Zustandsart }) {
  return (
    <svg
      className={`zustandssymbol ${art}`}
      viewBox="0 0 14 14"
      width="14"
      height="14"
      role="img"
      aria-label={benennung(art)}
    >
      {art === 'fehlt' && (
        // Als einzige leere Form — und gestrichelt, weil nichts da ist.
        <circle
          cx="7"
          cy="7"
          r="5.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeDasharray="2.4 2.4"
        />
      )}

      {art === 'offen' && (
        <>
          <circle cx="7" cy="7" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
          <path d="M7 1.5A5.5 5.5 0 0 1 7 12.5z" fill="currentColor" />
        </>
      )}

      {art === 'blockiert' && (
        // Achteck: die Stoppform, und die einzige mit Ecken.
        <path
          d="M4.6 0.8h4.8L13.2 4.6v4.8L9.4 13.2H4.6L0.8 9.4V4.6z"
          fill="currentColor"
        />
      )}

      {art === 'veraltet' && (
        <>
          <path
            d="M12.2 7a5.2 5.2 0 1 1-1.9-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          <path d="M12.6 0.6v3.6H9z" fill="currentColor" />
        </>
      )}

      {art === 'fertig' && (
        <>
          <circle cx="7" cy="7" r="6" fill="currentColor" />
          <path
            d="M4 7.2l2.1 2.1L10 5.4"
            fill="none"
            stroke="#fff"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      )}
    </svg>
  )
}
